import {
    registerPaymentMethod as wcRegisterPaymentMethod,
    registerExpressPaymentMethod as wcRegisterExpressPaymentMethod
} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {initStripe as loadStripe, getStripeAmount} from '@wc-stripe/blocks-utils';

/**
 * Returns the shared window state for express checkout availability.
 * Using window ensures a single shared instance regardless of how many
 * times this module is bundled across separate webpack entry points.
 */
const getExpressCheckoutState = () => {
    if (!window._wcStripeExpressCheckout) {
        window._wcStripeExpressCheckout = {paymentMethods: {}, promise: null};
    }
    return window._wcStripeExpressCheckout;
};

/**
 * Mounts a single hidden expressCheckout element to determine availability
 * for all registered express payment methods at once. The paymentMethods
 * map is fully assembled during synchronous registration before this is
 * ever called, so all display rules (auto/always) are captured in one mount.
 */
const getSharedAvailability = (cart) => {
    const state = getExpressCheckoutState();
    if (state.promise) return state.promise;

    let currencyCode = cart.cartTotals.currency_code;
    if (!currencyCode || currencyCode.length < 3) {
        currencyCode = getSetting('stripeGeneralData')?.currency;
    }
    const currency = currencyCode?.toLowerCase();
    // total_price is in WooCommerce's own configured minor unit, not necessarily Stripe's - see
    // getStripeAmount(). Needed here too since this mounts its own temporary element purely to
    // read availability, independent of the amount used once a method actually renders.
    const total = getStripeAmount(cart.cartTotals.total_price, {code: currencyCode, minorUnit: cart.cartTotals.currency_minor_unit});

    state.promise = new Promise(resolve => {
        loadStripe.then(stripe => {
            // Stripe.js failed to load, so no express payment methods are available.
            if (!stripe) {
                return resolve({});
            }
            let options = {mode: 'payment', currency};
            if (0 >= total) {
                options = {...options, mode: 'setup'};
            }
            if (['payment', 'subscription'].includes(options.mode)) {
                options.amount = total;
            }

            const element = stripe.elements(options);
            const expressCheckoutElement = element.create('expressCheckout', {
                paymentMethods: state.paymentMethods
            });

            const div = document.createElement('div');
            div.style.display = 'none';
            document.body.appendChild(div);

            expressCheckoutElement.on('ready', ({availablePaymentMethods}) => {
                expressCheckoutElement.unmount();
                div.remove();
                resolve(availablePaymentMethods ?? {});
            });
            expressCheckoutElement.on('loaderror', () => {
                div.remove();
                resolve({});
            });

            expressCheckoutElement.mount(div);
        });
    });

    return state.promise;
};

const buildCanMakePayment = (context, sections, availabilityKey) => ({cart}) => {
    const resolvedContext = typeof context === 'function' ? context() : context;
    if (!sections.includes(resolvedContext)) return false;
    return getSharedAvailability(cart).then(available => available[availabilityKey] ?? false);
};

/**
 * Wrapper around WooCommerce's registerPaymentMethod that wires up the shared
 * express checkout availability promise. Accepts all standard WC config plus:
 *
 * @param {Object}          activeMethod    - e.g. {applePay: 'auto'}
 * @param {string}          availabilityKey - Key to read from availablePaymentMethods
 * @param {string|Function} context         - Page context or function returning one
 * @param {string[]}        sections        - Gateway's enabled payment_sections
 */
export const registerPaymentMethod = ({activeMethod, availabilityKey, context, sections, ...wcConfig}) => {
    const state = getExpressCheckoutState();
    Object.assign(state.paymentMethods, activeMethod);
    wcRegisterPaymentMethod({...wcConfig, canMakePayment: buildCanMakePayment(context, sections, availabilityKey)});
};

/**
 * Wrapper around WooCommerce's registerExpressPaymentMethod. Same extra props
 * as registerPaymentMethod above.
 */
export const registerExpressPaymentMethod = ({activeMethod, availabilityKey, context, sections, ...wcConfig}) => {
    const state = getExpressCheckoutState();
    Object.assign(state.paymentMethods, activeMethod);
    wcRegisterExpressPaymentMethod({...wcConfig, canMakePayment: buildCanMakePayment(context, sections, availabilityKey)});
};
